import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { deleteItem, fetchData, handleAddItem, handleFilter, handleUpdateItem } from "../../utils/data";
import { PaginationType, Review, Trip } from "../../types";
import i18n from "../../i18n";
const lang = i18n.language === "ar" ? "ar" : "en";

export const fetchTrips = createAsyncThunk("get/Trip", async ({ userId, page }: { userId: number | string | undefined, page: number }) => {
    const data = await fetchData(`Trip/getTrips?lang=${lang}&userId=${userId}&page=${page}`, false, true);
    return data;
});

export const fetchPopularTrips = createAsyncThunk("get/PopularTrips", async ({ userId, page }: { userId: number | string | undefined, page: number }) => {
    const data = await fetchData(`Trip/getPopularTrips?lang=${lang}&userId=${userId}&page=${page}`, false, true);
    return data;
});
export const fetchTripDetails = createAsyncThunk("get/TripDetails", async ({ tripId, userId }: { tripId: number, userId?: string | number }) => {
    const data = await fetchData(`Trip/getTripDetails?tripId=${tripId}&userId=${userId}&lang=${lang}`);
    return data;
});
export const fetchTripDetailsWithAllData = createAsyncThunk("get/TripDetailsWithAllData", async ({ tripId }: { tripId: number }) => {
    const data = await fetchData(`Trip/getTripDetailsWithAllData?tripId=${tripId}`, true);
    return data;
});
export const fetchTripReviews = createAsyncThunk("get/TripReviews", async (tripId: number) => {
    const data = await fetchData(`Review/getTripReviews?tripId=${tripId}`);
    return data;
});

export const addTrip = createAsyncThunk("post/Trip", async (TripData: any) => {
    await handleAddItem(TripData, "Trip/addTrip", "POST");
});

export const updateTrip = createAsyncThunk("PUT/Trip", async ({ id, data }: { id: number, data: any }) => {
    await handleUpdateItem(data, `Trip/editTrip?TripId=${id}`, "PUT");
    return { ...data };
});
export const deleteTrip = createAsyncThunk("delete/Trip", async (id: number) => {
    await deleteItem(`Trip/deleteTrip?TripId=`, id);
    return id;
});
export const deleteTripImage = createAsyncThunk("delete/TripImage", async ({ tripId, imageId }: { tripId: number, imageId: number }) => {
    await deleteItem(`Trip/deleteTripImage?TripId=${tripId}&imageId=`, imageId,);
});

export const filterTrips = createAsyncThunk("post/filterTrips", async ({ filters, page }: { filters: any, page: number }) => {
    const response = await handleFilter(filters, `Trip/filterTrips?lang=${lang}&page=${page}`, "POST");
    return response;
});

interface TripState {
    trips: Trip[];
    popularTrips: Trip[];
    popularPagination: PaginationType | null;
    trip: Trip | null;
    reviews: Review[]
    pagination: PaginationType | null;
    loading: boolean;
    error: string;
}

const initialState: TripState = {
    trips: [],
    popularTrips: [],
    reviews: [],
    popularPagination: null,
    pagination: null,
    trip: null,
    loading: false,
    error: ""
};

const TripSlice = createSlice({
    name: "tripSlice",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchTrips.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(fetchTrips.fulfilled, (state, action) => {
                state.loading = false;
                state.trips = action.payload.data;
                state.pagination = action.payload.pagination;
            })
            // Fetch popular trips
            .addCase(fetchPopularTrips.pending, (state) => {
                state.error = "";
            })
            .addCase(fetchPopularTrips.fulfilled, (state, action) => {
                state.loading = false;
                state.popularTrips = action.payload.data;
                state.popularPagination = action.payload.pagination;
            })
            // Fetch trip Details
            .addCase(fetchTripDetails.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(fetchTripDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.trip = action.payload;
            })
            .addCase(fetchTripReviews.fulfilled, (state, action) => {
                state.reviews = action.payload;
            })
            // Fetch trip with all Details
            .addCase(fetchTripDetailsWithAllData.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(fetchTripDetailsWithAllData.fulfilled, (state, action) => {
                state.loading = false;
                state.trip = action.payload;
            })
            .addCase(filterTrips.pending, (state) => {
                state.error = "";
            })
            .addCase(filterTrips.fulfilled, (state, action) => {
                state.loading = false;
                state.trips = action.payload?.data;
                state.pagination = action.payload?.pagination;
            })
            // Delete
            .addCase(deleteTrip.fulfilled, (state, action) => {
                state.trips = state.trips.filter((item) => item.id !== action.payload);
            })
    }
});

export default TripSlice.reducer;
